import { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaEye, FaUndo, FaPlus, FaTrashAlt } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import ScaleLoader from "react-spinners/ScaleLoader";
import ConfirmModal from "../../components/ConfirmModal";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { formatNumber } from "../../utils/formatNumber";
import "../../Styles/yellowcalfcss/Finance.css";

/**
 * Journal Entries — manual multi-line postings to the General Ledger.
 *
 * Every entry must be balanced (sum of debits === sum of credits) before it
 * can be saved; the modal footer blocks the save until it is. Posted entries
 * are never edited or deleted — they are reversed, which posts a mirrored
 * contra entry, keeping the audit trail intact.
 */

const authPayload = () => ({
  customer_id: Number(localStorage.getItem("customer_id")),
  api_key: localStorage.getItem("api_key"),
  user_id: Number(localStorage.getItem("user_id")),
  language: "en",
});

const blankLine = () => ({
  account_id: "",
  line_description: "",
  debit: "",
  credit: "",
});

const today = () => new Date().toISOString().slice(0, 10);

export function JournalEntries() {
  const customer_currency = (
    localStorage.getItem("customer_currency") || ""
  ).replace(/"/g, "");

  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");

  // Create modal
  const [displayModal, setDisplayModal] = useState(false);
  const [header, setHeader] = useState({
    journal_date: today(),
    journal_reference: "",
    journal_narration: "",
  });
  const [lines, setLines] = useState([blankLine(), blankLine()]);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // View modal
  const [viewModal, setViewModal] = useState(false);
  const [viewEntry, setViewEntry] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  // Reverse confirm
  const [reverseTarget, setReverseTarget] = useState(null);
  const [isReversing, setIsReversing] = useState(false);

  /* ---------------- FETCH ---------------- */
  const fetchEntries = async () => {
    setLoading(true);

    const requestBody = {
      ...authPayload(),
      from_date: fromDate,
      to_date: toDate,
      search: search,
    };

    try {
      const res = await simplePostCall(
        ApiConfig.journalList,
        JSON.stringify(requestBody)
      );

      if (res?.result) {
        setEntries(res.data || []);
      } else {
        setEntries([]);
        toast.error(res?.message || "Failed to load journal entries");
      }
    } catch {
      setEntries([]);
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await simplePostCall(
        ApiConfig.coaList,
        JSON.stringify({ ...authPayload(), account_type: "", search: "" })
      );
      if (res?.result) setAccounts(res.data || []);
    } catch {
      // The list page surfaces COA failures; stay quiet here.
    }
  };

  useEffect(() => {
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchEntries();
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, search]);

  /* ---------------- LINE HELPERS ---------------- */
  const updateLine = (index, field, value) => {
    setLines((prev) =>
      prev.map((line, i) => {
        if (i !== index) return line;
        const next = { ...line, [field]: value };
        // A single line is either a debit or a credit, never both.
        if (field === "debit" && value !== "") next.credit = "";
        if (field === "credit" && value !== "") next.debit = "";
        return next;
      })
    );
  };

  const addLine = () => setLines((prev) => [...prev, blankLine()]);

  const removeLine = (index) =>
    setLines((prev) =>
      prev.length <= 2 ? prev : prev.filter((_, i) => i !== index)
    );

  const totalDebit = lines.reduce((sum, l) => sum + (Number(l.debit) || 0), 0);
  const totalCredit = lines.reduce((sum, l) => sum + (Number(l.credit) || 0), 0);
  const difference = totalDebit - totalCredit;
  const isBalanced = totalDebit > 0 && Math.abs(difference) < 0.005;

  /* ---------------- VALIDATION ---------------- */
  const validateForm = () => {
    const errors = {};
    if (!header.journal_date) errors.journal_date = "Entry date is required";

    const usableLines = lines.filter(
      (l) => l.account_id && (Number(l.debit) > 0 || Number(l.credit) > 0)
    );
    if (usableLines.length < 2)
      errors.lines = "At least two lines with an account and an amount are required";
    if (lines.some((l) => (Number(l.debit) > 0 || Number(l.credit) > 0) && !l.account_id))
      errors.lines = "Every line with an amount must have an account selected";
    if (!isBalanced)
      errors.balance = "Total debits must equal total credits before posting";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /* ---------------- CREATE ---------------- */
  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);

    const requestBody = {
      ...authPayload(),
      journal_date: header.journal_date,
      journal_reference: header.journal_reference,
      journal_narration: header.journal_narration,
      total_debit: Number(totalDebit.toFixed(2)),
      total_credit: Number(totalCredit.toFixed(2)),
      lines: lines
        .filter((l) => l.account_id && (Number(l.debit) > 0 || Number(l.credit) > 0))
        .map((l) => ({
          account_id: Number(l.account_id),
          line_description: l.line_description,
          debit: Number(l.debit) || 0,
          credit: Number(l.credit) || 0,
        })),
    };

    try {
      const res = await simplePostCall(
        ApiConfig.journalCreate,
        JSON.stringify(requestBody)
      );
      if (res?.result) {
        toast.success(res.message || "Journal entry posted");
        setDisplayModal(false);
        fetchEntries();
      } else {
        toast.error(res?.message || "Posting failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- VIEW ---------------- */
  const openView = async (record) => {
    setViewEntry(null);
    setViewModal(true);
    setViewLoading(true);

    try {
      const res = await simplePostCall(
        ApiConfig.journalView,
        JSON.stringify({ ...authPayload(), journal_id: record.journal_id })
      );
      if (res?.result) {
        setViewEntry(res.data || null);
      } else {
        toast.error(res?.message || "Failed to load entry");
        setViewModal(false);
      }
    } catch {
      toast.error("Network error");
      setViewModal(false);
    } finally {
      setViewLoading(false);
    }
  };

  /* ---------------- REVERSE ---------------- */
  const handleReverse = async () => {
    setIsReversing(true);

    try {
      const res = await simplePostCall(
        ApiConfig.journalReverse,
        JSON.stringify({
          ...authPayload(),
          journal_id: reverseTarget.journal_id,
          reversal_date: today(),
        })
      );
      if (res?.result) {
        toast.success(res.message || "Entry reversed");
        fetchEntries();
      } else {
        toast.error(res?.message || "Reversal failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setIsReversing(false);
      setReverseTarget(null);
    }
  };

  /* ---------------- HELPERS ---------------- */
  const openCreate = () => {
    setHeader({
      journal_date: today(),
      journal_reference: "",
      journal_narration: "",
    });
    setLines([blankLine(), blankLine()]);
    setFormErrors({});
    setDisplayModal(true);
  };

  const accountLabel = (accountId) => {
    const account = accounts.find(
      (a) => String(a.account_id) === String(accountId)
    );
    return account ? `${account.account_code} · ${account.account_name}` : "—";
  };

  const statusClass = (status) => {
    const s = String(status || "posted").toLowerCase();
    if (s === "reversed") return "fin-status fin-status-reversed";
    if (s === "draft") return "fin-status fin-status-draft";
    return "fin-status fin-status-posted";
  };

  const statusLabel = (status) =>
    ({ reversed: "Reversed", draft: "Draft", posted: "Posted" }[
      String(status || "posted").toLowerCase()
    ] || "Posted");

  const money = (value) => `${customer_currency} ${formatNumber(value || 0)}`;

  const hasFilters = fromDate !== "" || toDate !== "" || search !== "";

  const clearFilters = () => {
    setFromDate("");
    setToDate("");
    setSearch("");
  };

  // Summary across the entries currently in view.
  const summary = entries.reduce(
    (acc, e) => {
      const status = String(e.journal_status || "posted").toLowerCase();
      if (status === "reversed") acc.reversed += 1;
      else {
        acc.posted += 1;
        acc.debit += Number(e.total_debit) || 0;
        acc.credit += Number(e.total_credit) || 0;
      }
      return acc;
    },
    { posted: 0, reversed: 0, debit: 0, credit: 0 }
  );

  /* ---------------- UI ---------------- */
  return (
    <div className="finance-page-container">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER */}
      <div className="special-header panel-white-all">
        <h4 className="page-title">Journal Entries</h4>
        <div className="action-buttons-group">
          <Button className="btn btn-primary" onClick={openCreate}>
            New Journal Entry
          </Button>
        </div>
      </div>

      <div className="yc-list-body">
        {/* SUMMARY */}
        <div className="fin-stat-row">
          <div className="fin-stat-card fin-accent-all">
            <span className="fin-stat-label">Posted Entries</span>
            <span className="fin-stat-value">{summary.posted}</span>
            <span className="fin-stat-sub">In current view</span>
          </div>
          <div className="fin-stat-card fin-accent-asset">
            <span className="fin-stat-label">Total Debits</span>
            <span className="fin-stat-value">{money(summary.debit)}</span>
            <span className="fin-stat-sub">Posted entries only</span>
          </div>
          <div className="fin-stat-card fin-accent-income">
            <span className="fin-stat-label">Total Credits</span>
            <span className="fin-stat-value">{money(summary.credit)}</span>
            <span className="fin-stat-sub">Posted entries only</span>
          </div>
          <div className="fin-stat-card fin-accent-expense">
            <span className="fin-stat-label">Reversed</span>
            <span className="fin-stat-value">{summary.reversed}</span>
            <span className="fin-stat-sub">Excluded from totals</span>
          </div>
        </div>

        {/* FILTERS */}
        <div className="yc-list-filter">
          <Form.Control
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            title="From date"
          />
          <Form.Control
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            title="To date"
          />
          <Form.Control
            placeholder="Search reference or narration"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {hasFilters && (
            <Button variant="secondary" onClick={clearFilters}>
              Clear
            </Button>
          )}
        </div>

        {/* TABLE HEADER */}
        <div className="yc-list-thead-card">
          <div className="yc-list-grid yc-list-grid-8">
            <div className="yc-list-cell">Sr.No</div>
            <div className="yc-list-cell">Date</div>
            <div className="yc-list-cell">Reference</div>
            <div className="yc-list-cell">Narration</div>
            <div className="yc-list-cell">Debit</div>
            <div className="yc-list-cell">Credit</div>
            <div className="yc-list-cell">Status</div>
            <div className="yc-list-cell">Actions</div>
          </div>
        </div>

        {/* TABLE BODY */}
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "20vh" }}
          >
            <ScaleLoader color="#F4BB44" />
          </div>
        ) : entries.length === 0 ? (
          <div className="fin-empty">
            <span className="fin-empty-title">
              {hasFilters ? "No entries in this range" : "No journal entries yet"}
            </span>
            <span className="fin-empty-text">
              {hasFilters
                ? "No entries match the selected dates or search text."
                : "Journal entries are the manual postings that make up the general ledger. Every entry must balance — total debits equal total credits."}
            </span>
            {hasFilters ? (
              <Button variant="secondary" className="mt-2" onClick={clearFilters}>
                Clear Filters
              </Button>
            ) : (
              <Button className="btn btn-primary mt-2" onClick={openCreate}>
                Create First Entry
              </Button>
            )}
          </div>
        ) : (
          <div className="yc-list-row-list">
            {entries.map((record, index) => (
              <div key={record.journal_id} className="yc-list-row-card">
                <div className="yc-list-grid yc-list-grid-8">
                  <div className="yc-list-cell">{index + 1}</div>
                  <div className="yc-list-cell">
                    {record.journal_date
                      ? new Date(record.journal_date).toLocaleDateString()
                      : "—"}
                  </div>
                  <div className="yc-list-cell fw-semibold">
                    {record.journal_reference || "—"}
                  </div>
                  <div className="yc-list-cell" title={record.journal_narration}>
                    {record.journal_narration || "—"}
                  </div>
                  <div className="yc-list-cell">{money(record.total_debit)}</div>
                  <div className="yc-list-cell">{money(record.total_credit)}</div>
                  <div className="yc-list-cell">
                    <span className={statusClass(record.journal_status)}>
                      {statusLabel(record.journal_status)}
                    </span>
                  </div>
                  <div className="yc-list-cell yc-list-cell-actions">
                    <button
                      className="yc-action-icon"
                      title="View"
                      onClick={() => openView(record)}
                    >
                      <FaEye size={16} />
                    </button>
                    {String(record.journal_status).toLowerCase() !== "reversed" && (
                      <button
                        className="yc-action-icon yc-action-icon--danger"
                        title="Reverse entry"
                        onClick={() => setReverseTarget(record)}
                      >
                        <FaUndo size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CREATE MODAL */}
      <Modal
        show={displayModal}
        onHide={() => !saving && setDisplayModal(false)}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>New Journal Entry</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>
                  Entry Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={header.journal_date}
                  isInvalid={!!formErrors.journal_date}
                  onChange={(e) =>
                    setHeader({ ...header, journal_date: e.target.value })
                  }
                />
              </Form.Group>
            </div>
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Reference</Form.Label>
                <Form.Control
                  value={header.journal_reference}
                  onChange={(e) =>
                    setHeader({ ...header, journal_reference: e.target.value })
                  }
                  placeholder="e.g. JV-0001"
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Narration</Form.Label>
                <Form.Control
                  value={header.journal_narration}
                  onChange={(e) =>
                    setHeader({ ...header, journal_narration: e.target.value })
                  }
                  placeholder="What this entry records"
                />
              </Form.Group>
            </div>
          </div>

          {/* LINES */}
          {accounts.length === 0 && (
            <div className="fin-inline-warning">
              No GL accounts exist yet, so there is nothing to post against. Create
              them under <strong>Finance → Chart of Accounts</strong> first.
            </div>
          )}

          <div className="fin-lines-head">
            <div>Account</div>
            <div>Description</div>
            <div>Debit</div>
            <div>Credit</div>
            <div />
          </div>

          {lines.map((line, index) => (
            <div className="fin-lines-row" key={index}>
              <Form.Select
                value={line.account_id}
                onChange={(e) => updateLine(index, "account_id", e.target.value)}
              >
                <option value="">Select account</option>
                {accounts.map((a) => (
                  <option key={a.account_id} value={a.account_id}>
                    {a.account_code} · {a.account_name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control
                value={line.line_description}
                onChange={(e) =>
                  updateLine(index, "line_description", e.target.value)
                }
                placeholder="Line note"
              />
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={line.debit}
                onChange={(e) => updateLine(index, "debit", e.target.value)}
                placeholder="0.00"
              />
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={line.credit}
                onChange={(e) => updateLine(index, "credit", e.target.value)}
                placeholder="0.00"
              />
              <button
                type="button"
                className="yc-action-icon yc-action-icon--danger"
                title="Remove line"
                disabled={lines.length <= 2}
                onClick={() => removeLine(index)}
              >
                <FaTrashAlt />
              </button>
            </div>
          ))}

          <Button
            variant="link"
            className="fin-add-line-btn"
            onClick={addLine}
          >
            <FaPlus size={12} className="me-1" /> Add Line
          </Button>

          {/* TOTALS / BALANCE CHECK */}
          <div className="fin-totals-bar">
            <div className="fin-total-item">
              <span>Total Debit</span>
              <strong>{money(totalDebit)}</strong>
            </div>
            <div className="fin-total-item">
              <span>Total Credit</span>
              <strong>{money(totalCredit)}</strong>
            </div>
            <div className="fin-total-item">
              <span>Difference</span>
              <strong className={isBalanced ? "text-success" : "text-danger"}>
                {money(Math.abs(difference))}
              </strong>
            </div>
            <span
              className={
                isBalanced
                  ? "fin-balance-pill fin-balance-ok"
                  : "fin-balance-pill fin-balance-bad"
              }
            >
              {isBalanced ? "Balanced" : "Not Balanced"}
            </span>
          </div>

          {(formErrors.lines || formErrors.balance) && (
            <div className="text-danger mt-2" style={{ fontSize: 13 }}>
              {formErrors.lines || formErrors.balance}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setDisplayModal(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !isBalanced}>
            {saving ? <Spinner size="sm" /> : "Post Entry"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* VIEW MODAL */}
      <Modal show={viewModal} onHide={() => setViewModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Journal Entry Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewLoading ? (
            <div className="d-flex justify-content-center py-4">
              <ScaleLoader color="#F4BB44" />
            </div>
          ) : !viewEntry ? (
            <div className="yc-empty-state">No details available</div>
          ) : (
            <>
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <div className="fin-view-label">Date</div>
                  <div className="fin-view-value">
                    {viewEntry.journal_date
                      ? new Date(viewEntry.journal_date).toLocaleDateString()
                      : "—"}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="fin-view-label">Reference</div>
                  <div className="fin-view-value">
                    {viewEntry.journal_reference || "—"}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="fin-view-label">Status</div>
                  <div className="fin-view-value">
                    <span className={statusClass(viewEntry.journal_status)}>
                      {statusLabel(viewEntry.journal_status)}
                    </span>
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="fin-view-label">Narration</div>
                  <div className="fin-view-value">
                    {viewEntry.journal_narration || "—"}
                  </div>
                </div>
              </div>

              <div className="fin-view-lines-head">
                <div>Account</div>
                <div>Description</div>
                <div className="text-end">Debit</div>
                <div className="text-end">Credit</div>
              </div>
              {(viewEntry.lines || []).map((l, i) => (
                <div className="fin-view-lines-row" key={l.line_id || i}>
                  <div>
                    {l.account_code
                      ? `${l.account_code} · ${l.account_name}`
                      : accountLabel(l.account_id)}
                  </div>
                  <div>{l.line_description || "—"}</div>
                  <div className="text-end">
                    {Number(l.debit) > 0 ? money(l.debit) : "—"}
                  </div>
                  <div className="text-end">
                    {Number(l.credit) > 0 ? money(l.credit) : "—"}
                  </div>
                </div>
              ))}
              <div className="fin-view-lines-row fin-view-lines-total">
                <div className="fw-bold">Totals</div>
                <div />
                <div className="text-end fw-bold">
                  {money(viewEntry.total_debit)}
                </div>
                <div className="text-end fw-bold">
                  {money(viewEntry.total_credit)}
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* REVERSE CONFIRM */}
      <ConfirmModal
        show={!!reverseTarget}
        onHide={() => setReverseTarget(null)}
        title="Reverse Journal Entry"
        message={
          reverseTarget
            ? `This posts a mirrored contra entry for ${
                reverseTarget.journal_reference || "this entry"
              }. The original stays in the ledger for audit. Continue?`
            : ""
        }
        confirmText="Reverse"
        confirmVariant="danger"
        onConfirm={handleReverse}
        isLoading={isReversing}
      />
    </div>
  );
}

export default JournalEntries;
